/**
 * MHARHER - Create Content Page
 * Create Videos, Text Posts, and Polls
 * ADN MHARHER: Tunisian backgrounds, recipe linking, BARKA rewards
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  Video, Type, BarChart3, ChefHat, X, Upload, Plus, Trash2,
  Sparkles, MapPin, Hash, Check, ArrowRight, ArrowLeft, Music,
  Link as LinkIcon, Clock, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Tunisian themed backgrounds
const BACKGROUNDS = [
  { id: 'sidi_bousaid', name: 'Sidi Bou Saïd', gradient: 'linear-gradient(135deg, #1E4ED8 0%, #60A5FA 100%)', textColor: 'white' },
  { id: 'harissa', name: 'Harissa', gradient: 'linear-gradient(135deg, #D9381E 0%, #F2A900 100%)', textColor: 'white' },
  { id: 'bougainvillier', name: 'Bougainvillier', gradient: 'linear-gradient(135deg, #D946EF 0%, #F97316 100%)', textColor: 'white' },
  { id: 'safran', name: 'Safran', gradient: 'linear-gradient(135deg, #F2A900 0%, #FBBF24 100%)', textColor: '#1a1a1a' },
  { id: 'olive', name: 'Olive', gradient: 'linear-gradient(135deg, #4D7C0F 0%, #84CC16 100%)', textColor: 'white' },
  { id: 'mediterranean', name: 'Méditerranée', gradient: 'linear-gradient(135deg, #0D3A9E 0%, #3B82F6 100%)', textColor: 'white' },
  { id: 'sunrise_tunis', name: 'Lever de Soleil', gradient: 'linear-gradient(135deg, #F97316 0%, #FB7185 50%, #A855F7 100%)', textColor: 'white' },
  { id: 'desert', name: 'Désert', gradient: 'linear-gradient(135deg, #D4A574 0%, #F5E6D3 100%)', textColor: '#1a1a1a' },
  { id: 'mosaic', name: 'Mosaïque', gradient: 'linear-gradient(135deg, #1E4ED8 0%, #7C3AED 50%, #D946EF 100%)', textColor: 'white' },
  { id: 'plain_dark', name: 'Sombre', gradient: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', textColor: 'white' },
];

const POLL_DURATIONS = [
  { value: '24h', label: '24 heures' },
  { value: '48h', label: '48 heures' },
  { value: '3d', label: '3 jours' },
  { value: '1w', label: '1 semaine' },
];

const REGIONS = [
  { value: 'tunis', label: 'Tunis' },
  { value: 'sfax', label: 'Sfax' },
  { value: 'sousse', label: 'Sousse' },
  { value: 'monastir', label: 'Monastir' },
  { value: 'kairouan', label: 'Kairouan' },
  { value: 'bizerte', label: 'Bizerte' },
  { value: 'nabeul', label: 'Nabeul' },
  { value: 'djerba', label: 'Djerba' },
];

const FOOD_EMOJIS = ['🍲', '🥘', '🧆', '🥗', '🍖', '🍛', '🥙', '🍰', '🧁', '🍯', '🫒', '🌶️'];

export default function CreateContentPage() {
  const { api, user, isAuthenticated, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [contentType, setContentType] = useState(null); // 'video', 'text', 'poll'
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Common fields
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [region, setRegion] = useState('');

  // Text post fields
  const [textContent, setTextContent] = useState('');
  const [selectedBackground, setSelectedBackground] = useState(BACKGROUNDS[0]);
  const [fontStyle, setFontStyle] = useState('default');

  // Poll fields
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollEmojis, setPollEmojis] = useState(['', '']);
  const [pollDuration, setPollDuration] = useState('24h');

  // Video fields
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [musicName, setMusicName] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeSelector, setShowRecipeSelector] = useState(false);
  const [recipes, setRecipes] = useState([]);

  const videoInputRef = useRef(null);

  useEffect(() => {
    // Wait for auth to finish loading before redirecting
    if (!loading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, loading, navigate]);

  // Fetch user's recipes for linking
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserRecipes();
    }
  }, [isAuthenticated, user]);

  const fetchUserRecipes = async () => {
    try {
      const res = await api.get(`/recipes?creator_id=${user.id}&limit=50`);
      setRecipes(res.data || []);
    } catch (error) {
      console.error('Failed to fetch recipes:', error);
    }
  };

  const addHashtag = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const tag = hashtagInput.trim().replace(/^#/, '');
      if (tag && !hashtags.includes(tag) && hashtags.length < 10) {
        setHashtags([...hashtags, tag]);
        setHashtagInput('');
      }
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter(t => t !== tag));
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error(t('errors.fileTooLarge', 'Fichier trop volumineux (max 100MB)'));
      return;
    }

    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const addPollOption = () => {
    if (pollOptions.length < 4) {
      setPollOptions([...pollOptions, '']);
      setPollEmojis([...pollEmojis, '']);
    }
  };

  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
      setPollEmojis(pollEmojis.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const updatePollEmoji = (index, emoji) => {
    const newEmojis = [...pollEmojis];
    newEmojis[index] = emoji;
    setPollEmojis(newEmojis);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (contentType === 'text') {
        if (!textContent.trim()) {
          toast.error(t('errors.textRequired', 'Le texte est requis'));
          return;
        }

        await api.post('/feed/text-post', {
          text_content: textContent,
          background_type: selectedBackground.id,
          font_style: fontStyle,
          hashtags,
          region: region || null
        });

        toast.success(t('success.postCreated', 'Post créé! +10 BARKA'));
        navigate('/feed');
      } 
      else if (contentType === 'poll') {
        if (!pollQuestion.trim()) {
          toast.error(t('errors.questionRequired', 'La question est requise'));
          return;
        }
        
        const validOptions = pollOptions.filter(o => o.trim());
        if (validOptions.length < 2) {
          toast.error(t('errors.twoOptionsRequired', 'Minimum 2 options requises'));
          return;
        }

        await api.post('/feed/poll', {
          question: pollQuestion,
          options: validOptions,
          option_emojis: pollEmojis.slice(0, validOptions.length),
          duration: pollDuration,
          hashtags,
          region: region || null
        });

        toast.success(t('success.pollCreated', 'Sondage créé! +10 BARKA'));
        navigate('/feed');
      }
      else if (contentType === 'video') {
        if (!videoFile) {
          toast.error(t('errors.videoRequired', 'La vidéo est requise'));
          return;
        }

        // Upload video first
        const formData = new FormData();
        formData.append('file', videoFile);
        const uploadRes = await api.post('/upload/video', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Create feed item
        await api.post('/feed/video', {
          video_url: uploadRes.data.url,
          caption,
          hashtags,
          music_name: musicName || null,
          region: region || null,
          recipe_id: selectedRecipe?.id || null
        });

        toast.success(t('success.videoCreated', 'Vidéo publiée! +10 BARKA'));
        navigate('/feed');
      }
    } catch (error) {
      console.error('Failed to create content:', error);
      toast.error(t('errors.generic', 'Une erreur est survenue'));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while auth is checking
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F5F2] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1E4ED8] border-t-transparent"></div>
      </div>
    );
  }

  // Type selection screen
  if (!contentType) {
    return (
      <div className="min-h-screen bg-[#F8F5F2] p-6" data-testid="create-content-page">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center">
              <X className="w-5 h-5 text-gray-500" />
            </button>
            <h1 className="font-fraunces text-2xl font-bold text-gray-900">
              {t('create.title', 'Créer')}
            </h1>
            <div className="w-10" />
          </div>

          {/* Content Type Cards */}
          <div className="grid gap-4">
            {/* Video */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setContentType('video')}
              data-testid="create-video-card"
              className="relative bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] rounded-3xl p-6 text-left text-white overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
                  <Video className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold mb-1">{t('create.video', 'Vidéo')}</h3>
                <p className="text-white/80 text-sm">
                  {t('create.videoDesc', 'Partage tes recettes en vidéo. Lie une recette pour que tes abonnés puissent la suivre!')}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Badge className="bg-white/20 text-white border-0">
                    <ChefHat className="w-3.5 h-3.5 mr-1" />
                    {t('create.linkRecipe', 'Lier une recette')}
                  </Badge>
                </div>
              </div>
            </motion.button>

            {/* Text Post */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setContentType('text')}
              data-testid="create-text-card"
              className="relative bg-gradient-to-r from-[#D9381E] to-[#F2A900] rounded-3xl p-6 text-left text-white overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
                  <Type className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold mb-1">{t('create.textPost', 'Post Écrit')}</h3>
                <p className="text-white/80 text-sm">
                  {t('create.textPostDesc', 'Partage tes pensées, conseils ou astuces culinaires avec des fonds stylés.')}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Badge className="bg-white/20 text-white border-0">
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                    {t('create.tunisianThemes', 'Thèmes tunisiens')}
                  </Badge>
                </div>
              </div>
            </motion.button>

            {/* Poll */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setContentType('poll')}
              data-testid="create-poll-card"
              className="relative bg-gradient-to-r from-[#7C3AED] to-[#D946EF] rounded-3xl p-6 text-left text-white overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
                  <BarChart3 className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold mb-1">{t('create.poll', 'Sondage')}</h3>
                <p className="text-white/80 text-sm">
                  {t('create.pollDesc', 'Demande à ta communauté quelle recette tu devrais faire!')}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Badge className="bg-white/20 text-white border-0">
                    <Clock className="w-3.5 h-3.5 mr-1" />
                    {t('create.timed', 'Durée limitée')}
                  </Badge>
                </div>
              </div>
            </motion.button>
          </div>

          {/* BARKA Info */}
          <div className="mt-8 p-5 bg-gradient-to-r from-[#D9381E]/10 to-[#F2A900]/10 rounded-2xl border border-[#D9381E]/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D9381E]/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#D9381E]" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{t('create.earnBarka', 'Gagne des BARKA')}</p>
                <p className="text-sm text-gray-600">
                  {t('create.barkaReward', 'Chaque publication te rapporte +10 BARKA. Les likes et commentaires te rapportent encore plus!')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Text Post Creation
  if (contentType === 'text') {
    return (
      <div className="min-h-screen" style={{ background: selectedBackground.gradient }}>
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-50 p-4 flex items-center justify-between bg-black/20 backdrop-blur-sm">
          <button onClick={() => setContentType(null)} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-white font-semibold">{t('create.textPost', 'Post Écrit')}</h1>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !textContent.trim()}
            className="bg-white text-gray-900 hover:bg-white/90 rounded-full px-6"
          >
            {isLoading ? t('common.loading', 'Chargement...') : t('common.publish', 'Publier')}
          </Button>
        </div>

        {/* Content */}
        <div className="min-h-screen flex flex-col items-center justify-center px-6 pt-20 pb-32">
          <textarea
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            placeholder={t('create.whatOnMind', "Qu'est-ce qui te passe par la tête?")}
            maxLength={500}
            className={`
              w-full max-w-2xl text-center bg-transparent border-none outline-none resize-none
              placeholder:text-white/50
              ${fontStyle === 'artistic' ? 'font-fraunces italic text-3xl md:text-4xl' : 
                fontStyle === 'bold' ? 'font-black text-3xl md:text-4xl' : 
                'font-semibold text-2xl md:text-3xl'
              }
            `}
            style={{ color: selectedBackground.textColor }}
            rows={5}
            autoFocus
          />
          <p className="mt-4 text-sm" style={{ color: selectedBackground.textColor, opacity: 0.6 }}>
            {textContent.length}/500
          </p>
        </div>

        {/* Bottom Controls */}
        <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 space-y-4 max-h-[50vh] overflow-y-auto">
          {/* Background Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('create.background', 'Fond')}</label>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
              {BACKGROUNDS.map((bg) => (
                <button
                  key={bg.id}
                  onClick={() => setSelectedBackground(bg)}
                  className={`flex-shrink-0 w-16 h-16 rounded-xl transition-all ${
                    selectedBackground.id === bg.id ? 'ring-2 ring-[#1E4ED8] ring-offset-2' : ''
                  }`}
                  style={{ background: bg.gradient }}
                  title={bg.name}
                />
              ))}
            </div>
          </div>

          {/* Font Style */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('create.fontStyle', 'Style')}</label>
            <div className="flex gap-2">
              {['default', 'bold', 'artistic'].map((style) => (
                <button
                  key={style}
                  onClick={() => setFontStyle(style)}
                  className={`px-4 py-2 rounded-xl border transition-all ${
                    fontStyle === style 
                      ? 'bg-[#1E4ED8] text-white border-[#1E4ED8]' 
                      : 'bg-white text-gray-700 border-gray-200'
                  } ${
                    style === 'artistic' ? 'font-fraunces italic' : 
                    style === 'bold' ? 'font-black' : ''
                  }`}
                >
                  {style === 'default' ? 'Normal' : style === 'bold' ? 'Gras' : 'Artistique'}
                </button>
              ))}
            </div>
          </div>

          {/* Hashtags */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('create.hashtags', 'Hashtags')}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {hashtags.map((tag) => (
                <Badge key={tag} className="bg-[#1E4ED8]/10 text-[#1E4ED8] gap-1">
                  #{tag}
                  <button onClick={() => removeHashtag(tag)} className="ml-1 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400" />
              <Input
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyDown={addHashtag}
                placeholder={t('create.addHashtag', 'Ajouter un hashtag')}
                className="flex-1 border-gray-200"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Poll Creation
  if (contentType === 'poll') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1E4ED8] via-[#3B82F6] to-[#60A5FA] p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => setContentType(null)} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-white font-semibold text-lg">{t('create.poll', 'Sondage')}</h1>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2}
            className="bg-white text-gray-900 hover:bg-white/90 rounded-full px-6"
          >
            {isLoading ? t('common.loading', 'Chargement...') : t('common.publish', 'Publier')}
          </Button>
        </div>

        <div className="max-w-xl mx-auto space-y-6">
          {/* Question */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5">
            <label className="text-white/80 text-sm mb-2 block">{t('create.question', 'Ta question')}</label>
            <textarea
              value={pollQuestion}
              onChange={(e) => setPollQuestion(e.target.value)}
              placeholder={t('create.questionPlaceholder', 'Quelle recette je fais demain?')}
              maxLength={200}
              className="w-full bg-white/10 text-white placeholder:text-white/50 rounded-xl p-4 border-0 outline-none resize-none text-lg"
              rows={2}
            />
            <p className="text-white/50 text-xs mt-2 text-right">{pollQuestion.length}/200</p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="text-white/80 text-sm">{t('create.options', 'Options (2-4)')}</label>
            {pollOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2"
              >
                <button
                  onClick={() => {
                    const emoji = FOOD_EMOJIS[Math.floor(Math.random() * FOOD_EMOJIS.length)];
                    updatePollEmoji(index, emoji);
                  }}
                  className="w-12 h-12 rounded-xl bg-white/10 text-2xl flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  {pollEmojis[index] || '➕'}
                </button>
                <Input
                  value={option}
                  onChange={(e) => updatePollOption(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  className="flex-1 bg-white/10 border-0 text-white placeholder:text-white/50 h-12 rounded-xl"
                />
                {pollOptions.length > 2 && (
                  <button
                    onClick={() => removePollOption(index)}
                    className="w-10 h-10 rounded-xl bg-red-500/20 text-red-300 flex items-center justify-center hover:bg-red-500/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            ))}
            
            {pollOptions.length < 4 && (
              <button
                onClick={addPollOption}
                className="w-full py-3 rounded-xl border-2 border-dashed border-white/30 text-white/70 hover:border-white/50 hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                {t('create.addOption', 'Ajouter une option')}
              </button>
            )}
          </div>

          {/* Duration */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5">
            <label className="text-white/80 text-sm mb-3 block flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {t('create.duration', 'Durée du sondage')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {POLL_DURATIONS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setPollDuration(d.value)}
                  className={`py-3 rounded-xl text-sm font-medium transition-all ${
                    pollDuration === d.value
                      ? 'bg-white text-gray-900'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Hashtags */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5">
            <label className="text-white/80 text-sm mb-2 block">{t('create.hashtags', 'Hashtags')}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {hashtags.map((tag) => (
                <Badge key={tag} className="bg-white/20 text-white border-0 gap-1">
                  #{tag}
                  <button onClick={() => removeHashtag(tag)} className="ml-1 hover:text-red-300">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <Input
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyDown={addHashtag}
              placeholder={t('create.addHashtag', 'Ajouter un hashtag')}
              className="bg-white/10 border-0 text-white placeholder:text-white/50"
            />
          </div>
        </div>
      </div>
    );
  }

  // Video Creation
  if (contentType === 'video') {
    return (
      <div className="min-h-screen bg-[#F8F5F2] p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => setContentType(null)} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
            <h1 className="font-fraunces text-xl font-bold text-gray-900">{t('create.video', 'Vidéo')}</h1>
            <Button
              onClick={handleSubmit}
              disabled={isLoading || !videoFile}
              className="bg-[#1E4ED8] hover:bg-[#1E4ED8]/90 rounded-full px-6"
            >
              {isLoading ? t('common.loading', 'Chargement...') : t('common.publish', 'Publier')}
            </Button>
          </div>

          <div className="space-y-6">
            {/* Video Upload */}
            <div
              onClick={() => videoInputRef.current?.click()}
              className={`
                aspect-[9/16] max-h-[60vh] w-full max-w-sm mx-auto rounded-3xl border-2 border-dashed
                flex flex-col items-center justify-center cursor-pointer overflow-hidden transition-all
                ${videoPreview 
                  ? 'border-transparent' 
                  : 'border-gray-300 hover:border-[#1E4ED8] bg-white'
                }
              `}
            >
              {videoPreview ? (
                <video 
                  src={videoPreview} 
                  className="w-full h-full object-cover"
                  controls
                />
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-[#1E4ED8]" />
                  </div>
                  <p className="font-medium text-gray-900 mb-1">{t('create.uploadVideo', 'Importer une vidéo')}</p>
                  <p className="text-sm text-gray-500">{t('create.maxSize', 'Max 100MB')}</p>
                </>
              )}
            </div>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoSelect}
              className="hidden"
            />

            {/* Caption */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">{t('create.caption', 'Légende')}</label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder={t('create.captionPlaceholder', 'Décris ta vidéo...')}
                maxLength={300}
                rows={3}
                className="w-full rounded-2xl"
              />
            </div>

            {/* Link Recipe */}
            <div className="bg-gradient-to-r from-[#1E4ED8]/5 to-[#3B82F6]/5 rounded-2xl p-5 border border-[#1E4ED8]/20">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1E4ED8] flex items-center justify-center flex-shrink-0">
                  <ChefHat className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-1">{t('create.linkRecipe', 'Lier une recette')}</p>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('create.linkRecipeDesc', 'Tes abonnés pourront voir la recette complète directement depuis ta vidéo!')}
                  </p>
                  
                  {selectedRecipe ? (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                      {selectedRecipe.image_url ? (
                        <img src={selectedRecipe.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[#1E4ED8]/10 flex items-center justify-center">
                          <ChefHat className="w-6 h-6 text-[#1E4ED8]" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{selectedRecipe.title}</p>
                      </div>
                      <button 
                        onClick={() => setSelectedRecipe(null)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => setShowRecipeSelector(true)}
                      variant="outline"
                      className="w-full rounded-xl border-[#1E4ED8]/30 text-[#1E4ED8]"
                    >
                      <LinkIcon className="w-4 h-4 mr-2" />
                      {t('create.selectRecipe', 'Sélectionner une recette')}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Music */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                <Music className="w-4 h-4" />
                {t('create.musicName', 'Musique (optionnel)')}
              </label>
              <Input
                value={musicName}
                onChange={(e) => setMusicName(e.target.value)}
                placeholder={t('create.musicPlaceholder', 'Nom de la musique')}
                className="rounded-xl"
              />
            </div>

            {/* Hashtags */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">{t('create.hashtags', 'Hashtags')}</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {hashtags.map((tag) => (
                  <Badge key={tag} className="bg-[#1E4ED8]/10 text-[#1E4ED8] gap-1">
                    #{tag}
                    <button onClick={() => removeHashtag(tag)} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-gray-400" />
                <Input
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyDown={addHashtag}
                  placeholder={t('create.addHashtag', 'Ajouter un hashtag')}
                  className="flex-1 border-gray-200 rounded-xl"
                />
              </div>
            </div>

            {/* Region */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {t('create.region', 'Région (optionnel)')}
              </label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full p-3 rounded-xl border border-gray-200 bg-white"
              >
                <option value="">{t('create.selectRegion', 'Sélectionner une région')}</option>
                {REGIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Recipe Selector Modal */}
        <AnimatePresence>
          {showRecipeSelector && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setShowRecipeSelector(false)}
              />
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                className="fixed inset-x-0 bottom-0 h-[70vh] bg-white rounded-t-3xl z-50 flex flex-col"
              >
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{t('create.selectRecipe', 'Sélectionner une recette')}</h3>
                  <button onClick={() => setShowRecipeSelector(false)} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {recipes.length === 0 ? (
                    <div className="text-center py-12">
                      <ChefHat className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">{t('create.noRecipes', "Tu n'as pas encore de recettes")}</p>
                      <Button onClick={() => navigate('/create-recipe')} variant="outline" className="mt-4">
                        {t('create.createRecipe', 'Créer une recette')}
                      </Button>
                    </div>
                  ) : (
                    recipes.map((recipe) => (
                      <button
                        key={recipe.id}
                        onClick={() => {
                          setSelectedRecipe(recipe);
                          setShowRecipeSelector(false);
                        }}
                        className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors"
                      >
                        {recipe.image_url ? (
                          <img src={recipe.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-[#1E4ED8]/10 flex items-center justify-center">
                            <ChefHat className="w-8 h-8 text-[#1E4ED8]" />
                          </div>
                        )}
                        <div className="flex-1 text-left">
                          <p className="font-semibold text-gray-900">{recipe.title}</p>
                          <p className="text-sm text-gray-500">{recipe.category}</p>
                        </div>
                        <Check className="w-5 h-5 text-gray-400" />
                      </button>
                    ))
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return null;
}
